import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { forkJoin, Observable, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';

import { DealActions } from './deal.actions';
import { DealSelectors } from './deal.selectors';
import { DealDiffStatus } from './deal.state';
import { environment } from '../../environments/environment';
import { AdminService } from '../../features/admin/services/admin.service';
import { DealSectionShortCode } from '../../features/deal/constants/code-of-deal-keys';
import { DealService } from '../../features/deal/services/deal.service';
import { getDefaultDocument } from '../../features/deal/utils/deal-documents';
import {
  getDocumentTransactionLibraryId,
  getTransactionIdentifiers,
} from '../../features/document/constants/document-helper';
import { ViewerCommand } from '../../features/document-viewer/models/document-viewer.models';
import { DocumentViewerService } from '../../features/document-viewer/services/document-viewer.service';
import { ActivityEvent } from '../../features/home/models/activity-event.model';
import { IdentifierService } from '../../features/identifier/services/identifier.service';
import { isEmbedded } from '../../features/shared/constants/embedded.regex';
import { FAIL_NOTIFICATION_CONFIGURATION } from '../../features/shared/constants/notification-configurations';
import { Permission } from '../../features/shared/models/api/permissions.enum';
import { QualityTrafficLights } from '../../features/shared/models/common/quality-traffic-lights.enum';
import { PermissionService } from '../../features/shared/services/permission.service';
import { isTransientRequestFailure } from '../../features/shared/utils/is-transient-request-failure';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { ChecklistListSelectors } from '../checklist-list/checklist-list.selectors';
import { DocumentViewerActions } from '../document-viewer/document-viewer.actions';
import { EmbeddedActions } from '../embedded/embedded.actions';
import { ExtractActions } from '../extract/extract.actions';
import { ManagerActions } from '../manager/manager.actions';
import { RootState } from '../root-state.model';
import { TransactionSelectors } from '../transaction/transaction.selectors';

@Injectable()
export class DealEffects {
  private service = inject(DealService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly permissionService = inject(PermissionService);
  private readonly identifierService = inject(IdentifierService);
  private readonly documentViewerService = inject(DocumentViewerService);
  private readonly adminService = inject(AdminService);

  private readonly POLLING_INTERVAL = 3_000;

  syncDataOnly$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SyncDataOnly),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.adminService.singleSync(action.targetEnv, action.transactionId).pipe(
          map(() => DealActions.SyncDataOnlySuccess()),
          catchError((error) => {
            console.error(error);
            return of(DealActions.SyncDataOnlyFail({ error }));
          })
        )
      )
    )
  );

  syncDataOnlySuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SyncDataOnlySuccess),
      map(() => AppActions.PushNotification({ message: 'Data sync started', config: 'success' }))
    )
  );

  syncDataOnlyFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SyncDataOnlyFail),
      map(() => AppActions.PushNotification({ message: 'Data sync failed', config: 'fail' }))
    )
  );

  loadDealByTransactionId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealByTransactionId),
      withLatestFrom(this.store),
      map(([action, store]) =>
        DealActions.LoadDeal({
          transaction: store.transaction.list?.find(
            (t) => t.transactionId === action.transactionId
          ),
        })
      )
    )
  );

  loadDealEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDeal),
      withLatestFrom(this.permissionService.hasEveryPermission([Permission.EDITOR])),
      switchMap(([action, isEditor]) =>
        action.transaction.qualityTrafficLight === QualityTrafficLights.Green || isEditor
          ? this.service.getByTransaction(action.transaction).pipe(
              map((deal) => DealActions.LoadDealSuccess({ deal })),
              catchError((error) => {
                console.error(error);
                return of(DealActions.LoadDealFail({ error }));
              })
            )
          : of(DealActions.LoadDealFail({ error: 'qualityTrafficLight is not Green' }))
      )
    )
  );

  loadIdentifiersEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        DealActions.LoadDeal,
        ExtractActions.UpdateExtractOrderSuccess,
        EmbeddedActions.LoadDealSuccess
      ),
      withLatestFrom(
        this.store,
        isEmbedded() ? of(true) : this.permissionService.hasEveryPermission([Permission.EDITOR]),
        this.store.select(ChecklistListSelectors.selectedChecklistTopicIds)
      ),
      switchMap(([action, store, isEditor, selectedChecklistTopicIds]) => {
        const embedded = isEmbedded();
        const transaction = embedded
          ? store.embedded.deal
          : action.type === DealActions.LoadDeal.type
            ? action.transaction
            : store.deal.transaction;

        if (
          !embedded &&
          (!selectedChecklistTopicIds?.includes(DealSectionShortCode.Identifiers) || !transaction)
        ) {
          return of(DealActions.LoadIdentifiersSuccess({ identifiers: store.deal.identifiers }));
        }

        if (
          (!transaction?.qualityTrafficLight ||
            transaction.qualityTrafficLight === QualityTrafficLights.Grey ||
            transaction.qualityTrafficLight === QualityTrafficLights.Gray) &&
          !isEditor
        ) {
          return of(
            DealActions.LoadIdentifiersFail({ error: 'qualityTrafficLight is not Green' })
          );
        }

        return this.identifierService.getByTransaction(transaction).pipe(
          map((identifiers) => DealActions.LoadIdentifiersSuccess({ identifiers })),
          catchError((error) => {
            console.error(error);
            return of(DealActions.LoadIdentifiersFail({ error }));
          })
        );
      })
    )
  );

  extractDocument$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.ExtractTransaction),
      switchMap((action) =>
        this.service.extractDocument(action.transaction, action.document).pipe(
          map(() => DealActions.ExtractDocumentSuccess({ transaction: action.transaction })),
          reportEffectError(DealActions.ExtractDocumentFail)
        )
      )
    )
  );

  extractDocumentSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.ExtractDocumentSuccess),
      map(() =>
        AppActions.ShowInfoModal({
          data: {
            title: 'Success',
            text: 'Extraction in progress - You will receive a slack message when the extraction is complete.',
          },
        })
      )
    )
  );

  extractDocumentFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.ExtractDocumentFail),
      map(() => AppActions.PushNotification({ message: 'Failed to start the extraction', config: 'fail' }))
    )
  );

  setIsCompare$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SetIsCompare),
      filter((action) => !action.isCompare),
      map(() =>
        DealActions.SetOriginalDocumentId({
          originalDocumentId: undefined,
          originalDocumentTransactionLibraryId: undefined,
        })
      )
    )
  );

  setOriginalDocumentId$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SetOriginalDocumentId),
      map((action) =>
        action.originalDocumentId ? DealActions.StartLoadDealDiff() : DealActions.StopLoadDealDiff()
      )
    )
  );

  startLoadDealEffect$: Observable<any> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DealActions.StartLoadDealDiff),
        switchMap(() => {
          return timer(0, this.POLLING_INTERVAL).pipe(
            takeUntil(this.actions$.pipe(ofType(DealActions.StopLoadDealDiff))),
            map((pollingIndex) => {
              return DealActions.LoadDealDiff({ isFirst: !pollingIndex });
            })
          );
        })
      ),
    { dispatch: true }
  );

  loadDealDiff$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiff),
      withLatestFrom(this.store, this.store.select(DealSelectors.shelf)),
      map(([action, store, shelf]) => {
        const originalId = getTransactionIdentifiers(
          store.deal.originalDocumenTransactionLibrarytId
        );
        const finalId = getTransactionIdentifiers(
          store.documentViewer.documentTransactionLibraryId
        );

        return {
          originalDocument: shelf.find(
            (d) =>
              d.documentId === originalId.documentId &&
              d.transactionId === originalId.libraryTransactionId
          ),
          document: shelf.find(
            (d) =>
              d.documentId === finalId.documentId &&
              d.transactionId === finalId.libraryTransactionId
          ),
          isFirst: action.isFirst,
        };
      }),
      mergeMap(({ document, originalDocument, isFirst }) =>
        !!document && !!originalDocument
          ? this.service
              .diffExists(document, originalDocument)
              .pipe(map((path) => ({ path, isFirst })))
          : of({ isFirst, path: undefined })
      ),
      filter(({ path, isFirst }) => !!path || isFirst),
      switchMap(({ path, isFirst }) =>
        isFirst && !path
          ? of(DealActions.TriggerDealDiff())
          : forkJoin({
              diffUrl: this.service.diffUrl(path),
              textUrl: this.service
                .diffUrl(`${path}.domText.json`)
                .pipe(catchError(() => of(undefined))),
            }).pipe(
              map(({ diffUrl, textUrl }) => DealActions.LoadDealDiffSuccess({ diffUrl, textUrl }))
            )
      )
    )
  );

  addActivityTriggerEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.TriggerDealDiff),
      withLatestFrom(
        this.store.select(DealSelectors.finalDocument),
        this.store.select(DealSelectors.originalDocument)
      ),
      map(([action, finalDocument, originalDocument]) =>
        AppActions.AddActivity({
          activity: ActivityEvent.DocumentBlacklineGenerateView,
          meta: {
            url: window.location.href,
            originalDocumentId: originalDocument?.documentId,
            finalDocument: finalDocument?.documentId,
          },
        })
      )
    )
  );

  addActivityViewEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiffSuccess),
      withLatestFrom(
        this.store.select(DealSelectors.finalDocument),
        this.store.select(DealSelectors.originalDocument)
      ),
      map(([action, finalDocument, originalDocument]) =>
        AppActions.AddActivity({
          activity: ActivityEvent.DocumentBlacklineViewExisting,
          meta: {
            url: window.location.href,
            originalDocumentId: originalDocument?.documentId,
            finalDocument: finalDocument?.documentId,
          },
        })
      )
    )
  );

  loadDealDiffSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiffSuccess),
      map(() => DealActions.StopLoadDealDiff())
    )
  );

  loadDealDiffSuccessRefreshList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiffSuccess),
      map(() => DealActions.LoadAvailableDealDiffList())
    )
  );

  setDocumentLink$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiffSuccess),
      withLatestFrom(this.store),
      map(([action, store]) => {
        let link = `${environment.documentViewBaseUrl}?h=${btoa(action.diffUrl)}&ct`;

        if (action.textUrl) {
          link += `&htxt=${btoa(action.textUrl)}`;
        }

        return DocumentViewerActions.SetLink({
          link,
          documentId: store.documentViewer.documentId,
          documentTransactionLibraryId: store.documentViewer.documentTransactionLibraryId,
        });
      })
    )
  );

  documentLoaderStatus$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadDealDiffSuccess),
      withLatestFrom(this.store, this.store.select(DealSelectors.shelf)),
      switchMap(([action, store, shelf]) =>
        this.documentViewerService.listenOnDocumentEvents([ViewerCommand.ViewerInitialized]).pipe(
          take(1),
          map(() => DealActions.SetStatus({ diffStatus: DealDiffStatus.Completed }))
        )
      )
    )
  );

  setIsCompareLoadDiffList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.SetIsCompare),
      filter((action) => !!action.isCompare),
      map(() => DealActions.LoadAvailableDealDiffList())
    )
  );

  loadAvailableDealDiffList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.LoadAvailableDealDiffList),
      withLatestFrom(this.store.select(DealSelectors.finalDocument)),
      switchMap(([action, finalDocument]) =>
        this.service.diffList(finalDocument).pipe(
          map((availableDiffs) => DealActions.LoadAvailableDealDiffListSuccess({ availableDiffs })),
          catchError((error) => {
            console.error(error);
            return of(DealActions.LoadAvailableDealDiffListFail({ error }));
          })
        )
      )
    )
  );

  triggerDealDiff$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.TriggerDealDiff),
      withLatestFrom(
        this.store.select(DealSelectors.finalDocument),
        this.store.select(DealSelectors.originalDocument)
      ),
      switchMap(([action, finalDocument, originalDocument]) =>
        this.service.triggerDiff(originalDocument, finalDocument).pipe(
          map((isSucceeded) =>
            isSucceeded
              ? DealActions.TriggerDealDiffSuccess()
              : DealActions.TriggerDealDiffFail({ error: 'Failed to start the diff' })
          ),
          catchError((error) => {
            console.error(error);
            return of(DealActions.TriggerDealDiffFail({ error }));
          })
        )
      )
    )
  );

  triggerDealDiffFail$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.TriggerDealDiffFail),
      map(() =>
        AppActions.PushNotification({
          message: 'Failed to start the document blackline',
          config: FAIL_NOTIFICATION_CONFIGURATION,
        })
      )
    )
  );

  requestTransaction$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.RequestLoad),
      filter((action) => !!action.transaction),
      withLatestFrom(this.store),
      mergeMap(([{ transaction }, store]) => {
        const isSameTransaction =
          store.deal.transaction?.transactionId === transaction?.transactionId;

        const actions: Action[] = [
          DealActions.LoadDeal({ transaction }),
          ExtractActions.StartLoadList({ transactionIds: [transaction.transactionId] }),
          ManagerActions.LoadModel({ id: transaction.managerId }),
        ];

        if (!isSameTransaction) {
          const document = getDefaultDocument(transaction);

          actions.unshift(
            document
              ? DocumentViewerActions.OpenDocument({
                  transactionId: transaction.transactionId,
                  documentId: document.documentId,
                  documentTransactionLibraryId: getDocumentTransactionLibraryId(
                    document.documentId,
                    transaction.transactionId
                  ),
                })
              : DocumentViewerActions.ResetViewer()
          );
        }

        return actions;
      })
    )
  );

  redirectOnUnauthorized$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(DealActions.RequestLoad),
      filter((action) => !action.transaction),
      // `loadSettled` counts a failed load as settled, so absent-vs-failed needs the error too.
      withLatestFrom(this.store.select(TransactionSelectors.error)),
      filter(([, error]) => !error),
      map(() => AppActions.Unauthorized())
    )
  );
}
